import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Store, TrendingUp, ShieldCheck, Wallet, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [migrated, setMigrated] = useState(null); // { message } — legacy partner needs to activate

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMigrated(null);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      if (u.role === "platform_admin") nav("/admin");
      else if (u.role === "distributor") nav("/distributor");
      else nav("/");
    } catch (err) {
      // Phase C — detect migrated distributor accounts.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 410 && detail && typeof detail === "object" && detail.code === "migrated_to_shop") {
        setMigrated({ message: detail.message || "Your account has been upgraded to a HisaabPro Shop." });
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-10 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-[0.15]" />
        <div className="relative z-10">
          <div className="flex items-center gap-3" data-testid="login-brand">
            <Logo size={48} priority />
            <div className="font-display text-2xl font-bold">HisaabPro</div>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight">
            Hisaab strong, <br />
            recovery strong.
          </h1>
          <p className="text-slate-300 max-w-md">
            Installment business ka complete control system — customers, loans, payments aur recovery,
            sab ek hi jagah. Pakistan ke shop owners ke liye banaya gaya.
          </p>
          <div className="grid grid-cols-3 gap-3 max-w-md">
            {[
              { icon: Wallet, label: "Payments" },
              { icon: TrendingUp, label: "Recovery" },
              { icon: ShieldCheck, label: "Risk Check" },
            ].map((f) => (
              <div
                key={f.label}
                className="rounded-md border border-slate-700 bg-slate-800/50 backdrop-blur px-3 py-3"
              >
                <f.icon className="w-5 h-5 text-emerald-400" />
                <div className="text-sm font-medium mt-2">{f.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">
          Har shop ka apna data · mukammal tareeqay se mehfooz.
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="flex flex-col items-center lg:hidden mb-4">
            <Logo size={56} priority />
            <div className="font-display font-bold text-xl mt-2 text-slate-900">HisaabPro</div>
          </div>
          <div>
            <h2 className="font-display text-3xl font-bold">Welcome back</h2>
            <p className="text-slate-500 text-sm mt-1">Apni shop ka dashboard kholen.</p>
          </div>

          {migrated && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 space-y-3" data-testid="login-migrated-banner">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
                <div className="text-sm text-amber-900">
                  <div className="font-semibold mb-0.5">Referral Partner account upgraded</div>
                  <div className="text-xs">{migrated.message}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 flex-1"
                  onClick={() => window.open("https://wa.me/923718067478?text=Please%20resend%20my%20HisaabPro%20activation%20link", "_blank")}
                  data-testid="login-migrated-activate-btn"
                >
                  Get activation link
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="text-xs h-8 flex-1"
                  onClick={() => setMigrated(null)}
                  data-testid="login-migrated-dismiss-btn"
                >
                  Try different email
                </Button>
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email or username</Label>
            <Input
              id="email"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@shop.com or username"
              required
              data-testid="login-email-input"
              autoCapitalize="none"
              autoCorrect="off"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              data-testid="login-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="login-submit-btn"
          >
            {loading ? "Logging in..." : "Log in"}
          </Button>
          <div className="text-sm text-slate-600 text-center">
            New shop?{" "}
            <Link to="/register" className="text-emerald-700 font-semibold" data-testid="to-register-link">
              Create an account
            </Link>
          </div>
          <div className="text-xs text-slate-500 text-center pt-1">
            Refer &amp; Earn —{" "}
            <Link to="/distributor/register" className="text-emerald-700 font-semibold" data-testid="to-dist-register-link">
              Become a Referral Partner
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
